/**
 * MyTasksPage — WORKFLOW-OPS-001-PHASE-2A (P0-C / P0-D / P0-E)
 * =============================================================
 *
 * One canonical "My Tasks" surface reachable in 1 click from any
 * authenticated provider-role page. Renders different data sets per
 * role (P0-D task visibility rules):
 *
 *   - provider_admin       → all clinic tasks (full visibility)
 *   - provider_coordinator → all clinic tasks (full visibility)
 *   - provider_doctor      → tasks assigned to me only
 *   - provider_after_sales_rep → tasks assigned to me only
 *   - provider_sales_rep   → tasks assigned to me only
 *   - provider_accountant  → tasks assigned to me only (read-only)
 *
 * Reuses canonical endpoints — no new API:
 *   - GET /api/cases                              (admin/coord scope)
 *   - GET /api/milestone-tasks/case/{case_id}     (per-case fan-out)
 *   - GET /api/milestone-tasks/assignee/{user_id}?clinic_id=X
 *                                                 (individual contributor scope)
 *   - POST /api/milestone-tasks/{id}/transition   (status updates by row)
 *
 * No new collections. No RBAC redesign. Patient explicitly excluded
 * at the route level in App.js.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { formatDateLocale as _fmtDate } from '../../utils/dateFormat';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Building2,
  RefreshCw,
  AlertCircle,
  Inbox,
  ShieldCheck,
  ListChecks,
  Lock,
} from 'lucide-react';

import { api } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

// Roles allowed to mutate a task status from the My Tasks surface.
// Accountant is intentionally read-only (P0-D).
const ROW_WRITE_ROLES = new Set([
  'provider_admin',
  'provider_coordinator',
  'provider_doctor',
  'provider_after_sales_rep',
  'provider_sales_rep',
]);

// Roles that see every clinic task vs. only their own assignments.
const FULL_VISIBILITY_ROLES = new Set([
  'provider_admin',
  'provider_coordinator',
]);

const OUTSTANDING_STATUSES = new Set([
  'pending',
  'assigned',
  'in_progress',
  'blocked',
  'scheduled',
]);

const TERMINAL_STATUSES = new Set(['done', 'cancelled', 'skipped']);

const TASK_STATUS_VARIANT = {
  pending: 'bg-slate-100 text-slate-700 border-slate-200',
  assigned: 'bg-blue-50 text-blue-700 border-blue-200',
  in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
  blocked: 'bg-red-50 text-red-700 border-red-200',
  scheduled: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  done: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelled: 'bg-slate-50 text-slate-400 border-slate-200',
  skipped: 'bg-slate-50 text-slate-400 border-slate-200',
};

// Valid next-state transitions a row owner can drive from the My Tasks
// surface.  M16B-07D-B4-FC — Task Runtime Authority Closure (OPTION A).
// Status model is binary on the backend (satisfied / not-satisfied).  The
// FE picker mirrors that exactly: any non-terminal row can flip to `done`;
// a `done` row can flip back to `pending` (re-open).  Intermediate states
// like `in_progress` are dropped — they have no persisted backing.
const ROW_NEXT_STATUSES = {
  pending: ['done'],
  assigned: ['done'],
  in_progress: ['done'],
  scheduled: ['done'],
  blocked: ['done'],
  done: ['pending'],
};

function shortId(id) {
  if (!id || typeof id !== 'string') return '—';
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}…${id.slice(-4)}`;
}

function formatDateLocale(iso) {
  if (!iso) return '—';
  try {
    return _fmtDate(iso);
  } catch {
    return '—';
  }
}

function readCurrentUser() {
  try {
    return JSON.parse(localStorage.getItem('treatx_unified_user') || 'null') || {};
  } catch {
    return {};
  }
}

// M16B-07D-B4 — counters are computed in a non-hook helper so they sit
// outside the React hooks purity rule.  Pure over `tasks` + Date.now().
function computeCounts(tasks) {
  const now = Date.now();
  let outstanding = 0, blocked = 0, unassigned = 0, overdue = 0;
  for (const t of tasks) {
    const s = (t.status || '').toLowerCase();
    if (OUTSTANDING_STATUSES.has(s)) outstanding += 1;
    if (s === 'blocked') blocked += 1;
    if (!t.assigned_to_user_id && !t.assigned_role) unassigned += 1;
    const dueRaw = t.due_date || t.dueDate;
    if (dueRaw && !TERMINAL_STATUSES.has(s)) {
      const due = Date.parse(dueRaw);
      if (!Number.isNaN(due) && due < now) overdue += 1;
    }
  }
  return { outstanding, blocked, unassigned, overdue, total: tasks.length };
}

export default function MyTasksPage() {
  const { t: i18nT } = useTranslation();
  const navigate = useNavigate();
  const user = readCurrentUser();
  const role = (user.role || '').toLowerCase();
  const clinicId = user.clinic_id;
  const userId = user.id || user.user_id || user.email;
  const fullVisibility = FULL_VISIBILITY_ROLES.has(role);
  const canWriteRows = ROW_WRITE_ROLES.has(role);

  const [tasks, setTasks] = useState([]);
  const [agents, setAgents] = useState({}); // user_id → agent doc
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [filter, setFilter] = useState('outstanding'); // outstanding | all | terminal

  const refresh = useCallback(() => setRefreshTick((t) => t + 1), []);

  // 1. Load tasks per role visibility.
  useEffect(() => {
    if (!clinicId) return undefined;
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      setLoading(true);
      setError(null);
      try {
        // M16B-07D-B4 — My Tasks is now a VIEW of the canonical
        // patient_case_plans runtime authority.  One read, no fan-out, no
        // parallel task system.  Admins / coordinators may flip
        // scope=all to see every clinic-wide planner row; default is
        // `mine` (own assignments + role-inbox).
        const targetScope = fullVisibility ? 'all' : 'mine';
        const { data } = await api.get(`/clinic/planner/my-tasks?scope=${targetScope}`);
        const collected = Array.isArray(data?.tasks) ? data.tasks : [];
        if (!cancelled) setTasks(collected);
      } catch (e) {
        if (!cancelled) {
          setError(e?.response?.data?.detail || e?.response?.data?.message || e.message || 'Failed to load tasks');
          setTasks([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clinicId, userId, fullVisibility, refreshTick]);

  // 2. Optional: load clinic agents (admin/coord only) to resolve
  // assignee user_id → display name. Falls open if not permitted.
  useEffect(() => {
    if (!fullVisibility || !clinicId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/clinic/agents');
        if (cancelled) return;
        const map = {};
        (data?.agents || []).forEach((a) => {
          const k = a.user_id || a.agent_id;
          if (k) map[k] = a;
        });
        setAgents(map);
      } catch {
        /* fail-open: assignee shows user_id only */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fullVisibility, clinicId]);

  const filtered = useMemo(() => {
    if (filter === 'all') return tasks;
    if (filter === 'terminal') return tasks.filter((t) => TERMINAL_STATUSES.has((t.status || '').toLowerCase()));
    return tasks.filter((t) => OUTSTANDING_STATUSES.has((t.status || '').toLowerCase()));
  }, [tasks, filter]);

  const counts = computeCounts(tasks);

  const resolveAssignee = useCallback((t) => {
    const id = t.assigned_to_user_id;
    if (!id) return null;
    const a = agents[id];
    if (a && (a.display_name || a.name)) return a.display_name || a.name;
    return shortId(id);
  }, [agents]);

  const transitionRow = useCallback(async (taskId, target, task) => {
    // M16B-07D-B4 — My Tasks writes to the canonical patient_case_plans
    // overrides authority instead of milestone_tasks.  `done` maps to
    // resolve_row(resolved=true); reverting maps to resolve_row(resolved=false).
    // Intermediate states (in_progress / blocked) are recorded as
    // requirement_satisfactions side-effects through the same op so the
    // planner view always sees the latest state.
    const caseId = task?._case_id;
    if (!caseId) {
      toast.error('Cannot update task: case_id missing');
      return;
    }
    const resolved = target === 'done';
    try {
      await api.post(`/clinic/cases/${caseId}/plan/overrides`, {
        op: 'resolve_row',
        args: { row_id: taskId, resolved },
      });
      toast.success(`Task → ${target}`);
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.response?.data?.message || 'transition_failed';
      const msg = typeof detail === 'string' ? detail : (detail?.error || 'transition_failed');
      toast.error(`Could not change status: ${msg}`);
    }
  }, [refresh]);

  if (!clinicId) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center text-gray-600" data-testid="my-tasks-no-scope">
        <ShieldCheck className="h-10 w-10 text-amber-500 mx-auto mb-3" />
        <h1 className="text-xl font-semibold text-gray-900">{i18nT('provider.myTasks.noClinicScope')}</h1>
        <p className="mt-2 text-sm">
          {i18nT('provider.myTasks.noScopeHelp')}
        </p>
      </div>
    );
  }

  return (
    <div data-testid="my-tasks-page" className="min-h-[calc(100vh-4rem)] bg-gray-50">
      {/* Header band */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <Badge
              variant="outline"
              className="border-emerald-200 text-emerald-700 bg-emerald-50"
              data-testid="my-tasks-scope-badge"
            >
              {fullVisibility ? 'Clinic-wide' : 'Assigned to me'}
            </Badge>
            <h1 className="mt-2 text-2xl font-semibold text-gray-900" data-testid="my-tasks-title">
              My Tasks
            </h1>
            <div className="mt-1 flex items-center gap-2 text-sm text-gray-500">
              <Building2 className="h-4 w-4" />
              <span data-testid="my-tasks-clinic-id">Clinic {clinicId}</span>
              <span className="mx-1">·</span>
              <span data-testid="my-tasks-role">{role || 'unknown'}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={refresh}
              data-testid="my-tasks-refresh"
            >
              <RefreshCw className="h-4 w-4 mr-1.5" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-4">
        {/* Aggregate counts */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card data-testid="my-tasks-count-total">
            <CardContent className="p-4">
              <div className="text-[11px] uppercase tracking-wide text-gray-500">Total</div>
              <div className="text-2xl font-semibold text-gray-900">{loading ? '…' : counts.total}</div>
            </CardContent>
          </Card>
          <Card data-testid="my-tasks-count-outstanding">
            <CardContent className="p-4">
              <div className="text-[11px] uppercase tracking-wide text-gray-500">{i18nT('provider.myTasks.pendingItems')}</div>
              <div className={`text-2xl font-semibold ${counts.outstanding > 0 ? 'text-amber-600' : 'text-gray-900'}`}>
                {loading ? '…' : counts.outstanding}
              </div>
            </CardContent>
          </Card>
          <Card data-testid="my-tasks-count-blocked">
            <CardContent className="p-4">
              <div className="text-[11px] uppercase tracking-wide text-gray-500">Blocked</div>
              <div className={`text-2xl font-semibold ${counts.blocked > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                {loading ? '…' : counts.blocked}
              </div>
            </CardContent>
          </Card>
          <Card data-testid="my-tasks-count-overdue">
            <CardContent className="p-4">
              <div className="text-[11px] uppercase tracking-wide text-gray-500">Overdue</div>
              <div className={`text-2xl font-semibold ${counts.overdue > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                {loading ? '…' : counts.overdue}
              </div>
            </CardContent>
          </Card>
          {fullVisibility && (
            <Card data-testid="my-tasks-count-unassigned">
              <CardContent className="p-4">
                <div className="text-[11px] uppercase tracking-wide text-gray-500">Unassigned</div>
                <div className={`text-2xl font-semibold ${counts.unassigned > 0 ? 'text-amber-600' : 'text-gray-900'}`}>
                  {loading ? '…' : counts.unassigned}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Filter */}
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-500">Show:</span>
          {[
            ['outstanding', 'Pending Items'],
            ['terminal', 'Closed'],
            ['all', 'All'],
          ].map(([k, label]) => (
            <Button
              key={k}
              variant={filter === k ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(k)}
              data-testid={`my-tasks-filter-${k}`}
              className={filter === k ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : ''}
            >
              {label}
            </Button>
          ))}
        </div>

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ListChecks className="h-5 w-5 text-slate-500" />
              Tasks
              {!canWriteRows && (
                <Badge
                  variant="outline"
                  className="ml-2 border-slate-200 text-slate-500 bg-slate-50"
                  data-testid="my-tasks-readonly-badge"
                >
                  <Lock className="h-3 w-3 mr-1" />
                  Read-only
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="px-6 py-10 text-center text-gray-500" data-testid="my-tasks-loading">
                Loading tasks…
              </div>
            ) : error ? (
              <div className="px-6 py-10 text-center text-red-600" data-testid="my-tasks-error">
                <AlertCircle className="h-6 w-6 mx-auto mb-2" />
                {error}
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-6 py-10 text-center text-gray-500" data-testid="my-tasks-empty">
                <Inbox className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                {filter === 'outstanding'
                  ? 'No outstanding tasks. Nice work.'
                  : filter === 'terminal'
                    ? 'No closed tasks yet.'
                    : 'No tasks at this clinic yet.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200" data-testid="my-tasks-table">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">{i18nT('provider.myTasks.colTaskHeader')}</th>
                      <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">{i18nT('provider.myTasks.colStatusHeader')}</th>
                      <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">{i18nT('provider.coordDash.colAssignee')}</th>
                      <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">{i18nT('provider.coordDash.colRole')}</th>
                      <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">{i18nT('provider.myTasks.pendingItems')}</th>
                      <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">{i18nT('provider.myTasks.colDueHeader')}</th>
                      <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">{i18nT('provider.myTasks.colCaseHeader')}</th>
                      <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wide text-gray-500">{i18nT('provider.myTasks.colActions')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {filtered.map((t) => {
                      const tid = t.milestone_task_id || t.id;
                      const status = (t.status || '').toLowerCase();
                      const statusCls = TASK_STATUS_VARIANT[status] || TASK_STATUS_VARIANT.pending;
                      const pc = t.preconditions || {};
                      const docReqs = pc.required_documents || [];
                      const sigReqs = pc.required_signatures || [];
                      const pendingPC = [...docReqs, ...sigReqs].filter((r) => r.status === 'pending').length;
                      const totalPC = docReqs.length + sigReqs.length;
                      const allowed = ROW_NEXT_STATUSES[status] || [];
                      const isBlocked = status === 'blocked';
                      const assigneeName = resolveAssignee(t);
                      const caseId = t.case_id || t._case_id;
                      return (
                        <tr
                          key={tid}
                          className="hover:bg-slate-50/60"
                          data-testid={`my-tasks-row-${tid}`}
                        >
                          <td className="px-3 py-3 text-sm text-gray-800 max-w-[260px]">
                            <div className="font-medium truncate">{t.title || '(untitled)'}</div>
                            <div className="text-[11px] text-gray-400 font-mono">{shortId(tid)}</div>
                          </td>
                          <td className="px-3 py-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium ${statusCls}`} data-testid={`my-tasks-row-${tid}-status`}>
                              {status || 'unknown'}
                              {isBlocked && <Lock className="h-3 w-3 ml-1" />}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-700" data-testid={`my-tasks-row-${tid}-assignee`}>
                            {assigneeName || <span className="text-amber-600">Unassigned</span>}
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-500" data-testid={`my-tasks-row-${tid}-role`}>
                            {t.assigned_role || '—'}
                          </td>
                          <td className="px-3 py-3 text-sm text-right" data-testid={`my-tasks-row-${tid}-preconds`}>
                            {totalPC === 0 ? (
                              <span className="text-gray-400">0</span>
                            ) : (
                              <span className={pendingPC > 0 ? 'font-semibold text-amber-600' : 'text-emerald-600'}>
                                {pendingPC} / {totalPC}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-3 text-sm text-gray-500">{formatDateLocale(t.due_date || t.dueDate)}</td>
                          <td className="px-3 py-3 text-xs text-gray-500 font-mono">{shortId(caseId)}</td>
                          <td className="px-3 py-3 text-right whitespace-nowrap">
                            <div className="inline-flex items-center gap-1.5">
                              {canWriteRows && allowed.length > 0 && (
                                <select
                                  defaultValue=""
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    e.target.value = '';
                                    if (v) transitionRow(tid, v, t);
                                  }}
                                  data-testid={`my-tasks-row-${tid}-status-picker`}
                                  className="text-xs border border-gray-300 rounded px-1 py-1 bg-white"
                                >
                                  <option value="">{i18nT('provider.myTasks.setStatusPh')}</option>
                                  {allowed.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                  ))}
                                </select>
                              )}
                              {caseId && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => navigate(`/clinic/cases/${caseId}/workflow`)}
                                  data-testid={`my-tasks-row-${tid}-open`}
                                >
                                  Open
                                  <ArrowRight className="ml-1 h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="text-xs text-gray-400 px-1">
          {fullVisibility
            ? 'Showing all clinic tasks. Use the workflow page (Open) to perform admin actions (assign, transfer, suppress, add preconditions).'
            : 'Showing tasks assigned to you. Use Open to view full workflow context for that case.'}
        </div>

        <div>
          <Link to="/" className="text-sm text-slate-500 hover:text-slate-700" data-testid="my-tasks-home">
            ← Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}
